import classNames from 'classnames/bind';
import styles from '../Dashboard.module.scss';

import { Box, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import RenderCustomLegend from '~/components/pie-chart/RenderCustomLegend';
import { useResponsiveValue } from '~/hooks/useResponsiveValue';
import { IDevice } from '~/utils/interface/dashboard';

const cx = classNames.bind(styles);

const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, value, name, fill, responsiveLabel }: any) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  const sx = cx + outerRadius * cos;
  const sy = cy + (outerRadius + 5) * sin;
  const mx = cx + (outerRadius + 10) * cos;
  const my = cy + (outerRadius + 20) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * (responsiveLabel?.ex ?? 60);
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  const textPosition = responsiveLabel?.textPosition ?? 0;
  return value ? (
    <g>
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill='none' />
      <text
        x={(cos >= 0 ? mx + textPosition - 25 : mx - textPosition + 25) + (cos >= 0 ? 1 : -1) * 12}
        y={ey - 5}
        textAnchor={textAnchor}
        fill='#333'
        fontSize={responsiveLabel?.fontSize}
      >
        {`${name}`}
      </text>
      <text
        x={(cos >= 0 ? mx + textPosition : mx - textPosition) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill={fill}
        fontWeight={700}
        fontSize={responsiveLabel?.fontSize}
      >
        {`${value}`}
      </text>
    </g>
  ) : (
    <></>
  );
};
const DevicesSummary = (props: { device: IDevice | null }) => {
  const { t } = useTranslation('admin/home');
  const { device } = props;
  const [data, setData] = useState<Array<{ name: string; value: number; color: string }>>([]);

  const responsiveFontSize = useResponsiveValue({
    lg: '12px',
    xs: '10px',
    xl: '14px',
  });

  const responsiveLabel = useResponsiveValue({
    lg: {
      fontSize: '10px',
      textPosition: 15,
      ex: 50,
    },
    xl: {
      fontSize: '12px',
      textPosition: 35,
      ex: 80,
    },
  });

  useEffect(() => {
    if (device) {
      setData([
        {
          name: t('deviceSummary.connected'),
          value: device.connected,
          color: '#3D9149',
        },
        {
          name: t('deviceSummary.disconnected'),
          value: device.disconnected,
          color: '#ED1002',
        },
      ]);
    }
  }, [t, device]);
  return (
    <Box className={cx('w-[35%] h-full', 'dashboard-widget')}>
      <Typography className={cx('dashboard-widget-title', 'dashboard-widget-title-text')}>
        {t('totalDevices')}
      </Typography>
      <Box className={cx('dashboard-widget-body')}>
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Pie
              data={data}
              dataKey='value'
              nameKey='name'
              cx='50%'
              cy='50%'
              innerRadius='20%'
              outerRadius='60%'
              startAngle={90}
              endAngle={-270}
              fill='#82ca9d'
              labelLine={false}
              label={(prev) => renderCustomLabel({ ...prev, responsiveLabel })}
              // label
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <text
                x='50%'
                y='50%'
                textAnchor='middle'
                dominantBaseline='middle'
                fontSize={18}
                fontWeight='bold'
                fill='#005F73'
              >
                {device?.total ?? 0}
              </text>
            </Pie>
            <RenderCustomLegend fontSize={responsiveFontSize ?? '10px'} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default memo(DevicesSummary);
